import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';

const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes

/**
 * Session Heartbeat Hook
 * - Sends heartbeat every 5 minutes to keep session alive
 * - Tracks user activity (mouse, keyboard, touch)
 * - If idle for 30 min, backend returns IDLE_TIMEOUT → auto logout
 */
export function useSessionHeartbeat() {
  const { token, sendHeartbeat, logout } = useAuthStore();
  const lastActivityRef = useRef(Date.now());

  // Track user activity (resets idle timer)
  useEffect(() => {
    if (!token) return;

    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });
    window.addEventListener('click', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true });

    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('touchstart', onActivity);
      window.removeEventListener('click', onActivity);
      window.removeEventListener('scroll', onActivity);
    };
  }, [token]);

  // Heartbeat interval
  useEffect(() => {
    if (!token) return;

    console.log('[Heartbeat] Starting session heartbeat (5min interval)');

    const doHeartbeat = async () => {
      // Check client-side idle first (avoid unnecessary network call)
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs > IDLE_TIMEOUT) {
        console.log('[Heartbeat] Idle timeout (client-side) — logging out');
        logout();
        return;
      }

      try {
        await sendHeartbeat();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes('401') || msg.includes('IDLE_TIMEOUT')) {
          console.log('[Heartbeat] Session expired — logging out');
          logout();
        }
      }
    };

    // Initial heartbeat
    doHeartbeat();

    const interval = setInterval(doHeartbeat, HEARTBEAT_INTERVAL);

    return () => {
      console.log('[Heartbeat] Stopping session heartbeat');
      clearInterval(interval);
    };
  }, [token, sendHeartbeat, logout]);
}
